/* eslint-disable @typescript-eslint/no-unused-vars */
import styled, { css } from 'styled-components';
import { themeToken } from 'src/components/common/cpds/utils/cpds-utils';
import { Typo } from 'src/components/common/ui';
import { checkValue } from 'src/components/common/helper';
import { cpdsMediaQueryVals } from 'src/components/common/cpds/token/shared/common';
import { responsiveClass } from 'src/components/common/utils/dynamicClassSelector';

export const HeroOneKeyLogo = styled.div.attrs({
  'data-cy': 'heroonekeylogo',
})`
  margin-left: auto;
  .v3-hero-one-key-logo {
    height: auto;
    max-height: clamp(18px, calc((18 / 1280) * 100vw), calc((18 / 1280) * 1680px));
    width: clamp(268.5px, calc((268.5 / 1280) * 100vw), calc((268.5 / 1280) * 1680px));
    max-width: clamp(268.5px, calc((268.5 / 1280) * 100vw), calc((268.5 / 1280) * 1680px));
    img {
      max-height: clamp(18px, calc((18 / 1280) * 100vw), calc((18 / 1280) * 1680px));
      object-fit: cover;
    }
  }
`;

export const HeroBreadCrumbWrapper = styled.div.attrs({
  'data-cy': 'herobreadcrumbwrapper',
})`
  ${({
    theme: {
      styles: { cpds },
    },
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      position: absolute;
      top: ${token?.spacing?.space_56};
      left: ${token?.spacing?.space_56};
      width: 50%;
      z-index: 3;
      @media only screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
        width: 100%;
      }
    `;
  }}
`;

export const HeroVideoController = styled.button.attrs({
  'data-cy': 'herovideocontroller',
  className: 'hero-video-controller',
})<{ color?: string }>`
  ${({
    theme: {
      styles: { cpds },
    },
    color,
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      z-index: 3;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      position: absolute;
      bottom: ${token?.spacing?.space_56};
      right: ${token?.spacing?.space_56};
      background-color: transparent;
      border: 2px solid ${checkValue(color) ? color : token?.v3_article_hero_video_icon_color};
      svg {
        width: 12px;
        height: auto;
        min-height: 14px;
      }
      path {
        fill: ${checkValue(color) ? color : token?.v3_article_hero_video_icon_color};
      }
    `;
  }}
`;

export const HeroDescription = styled(Typo.HTMLText).attrs({
  tag: 'p',
  exClass: 'cpds-body-roman-medium-08',
  'data-cy': 'herodescription',
})<{ color?: string }>`
  ${({
    theme: {
      styles: { cpds },
    },
    color,
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      text-align: left;
      font-weight: 400 !important;
      color: ${checkValue(color) ? color : token?.v3_article_hero_description_color};
    `;
  }}
`;

export const HeroSubText = styled(Typo.HTMLText).attrs({
  tag: 'p',
  exClass: 'cpds-meta-roman-book-10',
  'data-cy': 'herosubtext',
})<{ color?: string }>`
  ${({
    theme: {
      styles: { cpds },
    },
    color,
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      text-align: left;
      font-weight: 400 !important;
      color: ${checkValue(color) ? color : token?.v3_article_hero_sub_text_color};
    `;
  }}
`;

export const HeroSubTextRow = styled.div.attrs({
  'data-cy': 'herosubtextrow',
})<{
  gap?: string;
  maxWidth?: string;
  direction?: 'row' | 'column';
}>`
  ${({
    theme: {
      styles: { cpds },
    },
    gap = '24px',
    direction = 'row',
    maxWidth = 'none',
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      display: flex;
      flex-direction: ${direction};
      gap: ${gap};
      margin-top: ${token?.spacing?.space_8};
      max-width: ${maxWidth};
      ${maxWidth !== 'none' ? `width: 100%;` : ``};
    `;
  }}
`;

export const HeroItemsColumn = styled.div.attrs({
  'data-cy': 'heroitemscolumn',
})`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
  max-width: ${({ maxWidth = 'none' }) => maxWidth} !important;
  ${({ maxWidth = 'none' }) => (maxWidth !== 'none' ? `width: 100%` : '')}
`;

export const HeroItemRow = styled.div.attrs({
  'data-cy': 'heroitemsrow',
})<{
  gap?: string;
  marginTop?: string;
  align?: 'center' | 'flex-start' | 'flex-end';
  justify?: 'center' | 'flex-start' | 'flex-end' | 'space-between';
}>`
  ${({ gap = '24px', align = 'center', justify = 'space-between', marginTop = '0px' }) => {
    const tabletGap = parseInt(gap, 10) / 2;

    return css`
      display: flex;
      flex-direction: row;
      gap: ${gap};
      align-items: ${align};
      justify-content: ${justify};
      width: 100%;
      margin-top: ${marginTop};

      @media only screen and (min-width: ${cpdsMediaQueryVals.cpds_medium_lower_limit}) and (max-width: ${cpdsMediaQueryVals.cpds_medium_upper_limit}) {
        gap: ${tabletGap}px;
      }
    `;
  }}
`;

export const HeroHeadline = styled(Typo.HTMLText).attrs(({ size = 'LARGE', children, isNeedDynamicClass }) => {
  const dynamicClass = responsiveClass({ text: children, defaulFontClass: 'cpds-display-roman-book-02', tag: 'h1' });
  return {
    tag: isNeedDynamicClass ? (checkValue(dynamicClass.tagName) ? dynamicClass.tagName : 'h1') : 'h1',
    // tag: `${size === 'SMALL' ? 'h3' : 'h2'}`,
    exClass: isNeedDynamicClass ? `${dynamicClass.className}` : 'cpds-display-roman-book-02',
    // exClass: `${size === 'SMALL' ? 'cpds-display-roman-book-03' : 'cpds-display-roman-book-02'}`,
    'data-cy': 'heroheadline',
  };
})<{ color?: string; size: 'SMALL' | 'LARGE'; maxWidth?: string }>`
  ${({
    theme: {
      styles: { cpds },
    },
    color,
    size = 'LARGE',
    maxWidth = 'none',
    heroTextColor,
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      text-align: left;
      color: ${checkValue(color) ? color : heroTextColor === 'light' ? '#FFF' : token?.v3_article_hero_headline_color};
      max-width: ${maxWidth} !important;
      @media only screen and (min-width: ${cpdsMediaQueryVals.cpds_medium_lower_limit}) and (max-width: ${cpdsMediaQueryVals.cpds_medium_upper_limit}) {
        ${HeroHeadline} {
          font-size: ${size === 'SMALL'
            ? `font-size: clamp(2.5rem, 4.0029rem + -3.1311vw, 1.5rem);`
            : `clamp(2.5rem, 0.9971rem + 3.1311vw, 3.5rem)`};
          line-height: normal;
        }
      }
    `;
  }}
`;

export const HeroEyebrow = styled(Typo.HTMLText).attrs({
  tag: 'h6',
  exClass: 'cpds-heading-roman-book-lite-06',
  'data-cy': 'heroeyebrow',
})<{ color?: string; isTopOrBottomVariation?: boolean }>`
  ${({
    theme: {
      styles: { cpds },
    },
    color,
    isTopOrBottomVariation = false,
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      text-align: left;
      color: ${checkValue(color) ? color : token?.v3_article_hero_headline_color};
      ${isTopOrBottomVariation
        ? css`
            font-size: 24px !important;
            line-height: 32px !important;
          `
        : ''}
    `;
  }}
`;

export const HeroDescriptionContainer = styled.div.attrs({
  'data-cy': 'herodescriptioncontainer',
})`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const HeroTitleContainer = styled.div.attrs({
  'data-cy': 'herotitlecontainer',
})`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const HeroLogoContainer = styled.div.attrs({
  'data-cy': 'heroloogcontainer',
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  .v3-article-hero-partner-logo {
    width: 100%;
    height: 100%;
    max-width: 48px;
    max-height: 48px;
    object-fit: cover;
  }
`;

export const HeroContentannel = styled.div.attrs({
  'data-cy': 'herocontentannel',
})`
  ${({
    theme: {
      styles: { cpds },
    },
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      display: flex;
      padding: ${token?.spacing?.space_56} 0 ${token?.spacing?.space_56};
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
    `;
  }}
`;

export const HeroBackgroundOverlay = styled.div.attrs({
  className: 'v3-hero-gradient-module',
})<{ opacity?: 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 }>`
  ${({
    theme: {
      styles: { cpds },
    },
    opacity,
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);

    return css`
      position: absolute;
      z-index: 2;
      background-color: #000000;
      opacity: ${checkValue(opacity) ? opacity : 0.3};
    `;
  }}
`;

export const HeroImagePannel = styled.div.attrs({
  'data-cy': 'heroimagepannel',
  id: 'heroImagePannel',
})`
  ${({
    theme: {
      styles: { cpds },
    },
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      display: flex;
      position: relative;
      flex: 1;
      padding: ${token?.v3_article_hero_image_panel_padding};
      box-sizing: border-box;
      .v3-article-hero-image,
      .v3-article-hero-video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: ${token?.v3_article_hero_image_border_radius};
      }
      ${HeroBackgroundOverlay} {
        top: ${token?.v3_article_hero_image_panel_padding};
        left: ${token?.v3_article_hero_image_panel_padding};
        bottom: ${token?.v3_article_hero_image_panel_padding};
        right: ${token?.v3_article_hero_image_panel_padding};
        border-radius: ${token?.v3_article_hero_image_border_radius};
      }
    `;
  }}
`;

const HeroLayoutConst = (token, turnOffBreadcrumbs = false) => ({
  IMAGE_LEFT: {
    desktop: {
      direction: 'row',
      padding: `${token?.spacing?.space_56} 0 ${token?.spacing?.space_56}`,
      height: '700px',
    },
    tablet: {
      padding: `${token?.spacing?.space_40} 0 ${token?.spacing?.space_40};`,
    },
    mobile: {
      direction: 'column-reverse',
      padding: turnOffBreadcrumbs ? `32px 0 0` : `76px 0 0;`,
      padding_content: `0 ${token?.spacing?.space_16} ${token?.spacing?.space_24}`,
      breadcrumbTop: `${token?.spacing?.space_32}`,
      breadcrumbLeft: `${token?.spacing?.space_16}`,
      breadCrumbExPadding: '0px',
    },
  },
  IMAGE_TOP: {
    desktop: {
      direction: 'column',
      padding: `${token?.spacing?.space_40} 0 ${token?.spacing?.space_56}`,
      height: 'auto',
    },
    tablet: {
      padding: `${token?.spacing?.space_40} 0`,
    },
    mobile: {
      direction: 'column',
      padding: `${token?.spacing?.space_0}`,
      padding_content: `${token?.spacing?.space_24} ${token?.spacing?.space_16} ${token?.spacing?.space_32}`,
      breadcrumbTop: `${token?.spacing?.space_16}`,
      breadcrumbLeft: `${token?.spacing?.space_16}`,
      breadCrumbExPadding: `${token?.v3_article_hero_image_panel_padding}`,
    },
  },
  IMAGE_BOTTOM: {
    desktop: {
      direction: 'column-reverse',
      padding: `${token?.spacing?.space_56} 0 ${token?.spacing?.space_40}`,
      height: 'auto',
    },
    tablet: {
      padding: `${token?.spacing?.space_40} 0`,
    },
    mobile: {
      direction: 'column-reverse',
      padding: turnOffBreadcrumbs
        ? `32px ${token?.spacing?.space_0} ${token?.spacing?.space_0}`
        : `76px ${token?.spacing?.space_0} ${token?.spacing?.space_0}`,
      padding_content: `0 ${token?.spacing?.space_16} ${token?.spacing?.space_24}`,
      breadcrumbTop: `${token?.spacing?.space_32}`,
      breadcrumbLeft: `${token?.spacing?.space_16}`,
      breadCrumbExPadding: `0px`,
    },
  },
});

const bgColorMapping = (token) => ({
  PRIMARY: token?.v3_article_hero_primary_bg_color,
  SECONDARY: token?.v3_article_hero_secondary_bg_color,
  TERTIARY: token?.v3_article_hero_tertiary_bg_color,
  ONEKEY_PRIMARY: token?.v3_article_hero_onekey_primary_bg_color,
  ONEKEY_SECONDARY: token?.v3_article_hero_onekey_secondary_bg_color,
});

export const textColorMapping = (token) => ({
  PRIMARY: token?.v3_article_hero_primary_text_color,
  SECONDARY: token?.v3_article_hero_secondary_text_color,
  TERTIARY: token?.v3_article_hero_tertiary_text_color,
  ONEKEY_PRIMARY: token?.v3_article_hero_onekey_primary_text_color,
  ONEKEY_SECONDARY: token?.v3_article_hero_onekey_secondary_text_color,
});

interface HeroMainContainerProps {
  variation?: 'IMAGE_LEFT' | 'IMAGE_RIGHT' | 'IMAGE_TOP' | 'IMAGE_BOTTOM';
  bgColor?: 'PRIMARY' | 'SECONDARY';
  contentStyle?: 'MAX' | 'MIN';
  turnOffBreadcrumbs?: boolean;
}

export const HeroMainContainer = styled.div.attrs({
  'data-cy': 'herocontainer',
})<HeroMainContainerProps>`
  ${({
    theme: {
      styles: { cpds },
    },
    variation = 'IMAGE_LEFT',
    bgColor = 'PRIMARY',
    contentStyle = 'MAX',
    turnOffBreadcrumbs = false,
    props,
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);

    const isOnekey = checkValue(bgColor) && bgColor.includes('ONEKEY');

    const heroVariation = HeroLayoutConst(token, turnOffBreadcrumbs)?.hasOwnProperty(variation)
      ? variation
      : 'IMAGE_LEFT';

    const heroMobileVariation = isOnekey && heroVariation === 'IMAGE_LEFT' ? 'IMAGE_TOP' : heroVariation;

    const heroVariationObj = HeroLayoutConst(token, turnOffBreadcrumbs)?.[heroVariation];
    const heroVariationObjMobile = HeroLayoutConst(token, turnOffBreadcrumbs)?.[heroMobileVariation];
    const heroBgColor = checkValue(bgColorMapping(token)?.[bgColor])
      ? bgColorMapping(token)?.[bgColor]
      : token?.v3_article_hero_primary_bg_color;

    const isToporBottomVariation = variation === 'IMAGE_TOP' || variation === 'IMAGE_BOTTOM';

    const heroTextColor = checkValue(textColorMapping(token)?.[bgColor])
      ? textColorMapping(token)?.[bgColor]
      : token?.v3_article_hero_primary_text_color;

    return css`
      width: 100%;
      max-width: ${token?.v3_parent_grid_max_width};
      margin: 0 auto;
      display: flex;
      flex-direction: ${heroVariationObj?.desktop?.direction};
      background-color: ${heroBgColor};
      height: ${heroVariationObj?.desktop?.height};
      position: relative;

      ${HeroContentannel} {
        padding: ${heroVariationObj?.desktop?.padding};
      }

      ${heroTextColor === 'light'
        ? css`
            h2,
            h6,
            p:not(.cpds-sponsor-tag p) {
              color: #ffffff !important;
            }
          `
        : ``}

      ${bgColor.includes('ONEKEY')
        ? css`
            h2,
            h6,
            p {
              color: ${token?.v3_article_hero_headline_color} !important;
            }
          `
        : ``}

      ${isToporBottomVariation
        ? css`
            ${HeroImagePannel} {
              width: 100%;
              height: ${token?.v3_article_hero_top_image_variation_height};
              max-height: ${token?.v3_article_hero_top_image_variation_height};
              max-width: 100%;
              .v3-article-hero-image,
              .v3-article-hero-video {
                height: calc(
                  ${token?.v3_article_hero_top_image_variation_height} - ${token?.v3_article_hero_image_panel_padding} -
                    ${token?.v3_article_hero_image_panel_padding}
                );
                max-height: calc(
                  ${token?.v3_article_hero_top_image_variation_height} - ${token?.v3_article_hero_image_panel_padding} -
                    ${token?.v3_article_hero_image_panel_padding}
                );
              }
            }
            ${HeroContentannel} {
              width: 100%;
              height: 100%;
              max-width: 100%;
              gap: 40px;
            }
            ${HeroTitleContainer} {
              max-width: none;
              gap: 12px;
            }
            ${HeroHeadline} {
              width: 100%;
              max-width: 758px;
            }

            @media only screen and (min-width: ${cpdsMediaQueryVals.cpds_medium_lower_limit}) and (max-width: ${cpdsMediaQueryVals.cpds_medium_upper_limit}) {
              ${HeroImagePannel} {
                aspect-ratio: 16/9;
                height: 100%;
                .v3-article-hero-image,
                .v3-article-hero-video {
                  height: auto;
                  max-height: none;
                }
              }
            }

            @media only screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
              ${HeroItemRow} {
                flex-direction: column-reverse;
                max-width: none !important;
                gap: 16px;
                align-items: flex-start;
                ${HeroSubTextRow} {
                  max-width: none;
                }
              }
            }
          `
        : css`
            ${HeroImagePannel} {
              margin-left: -56px;
              height: ${heroVariationObj?.desktop?.height};
            }
          `}

      @media only screen and (min-width: ${cpdsMediaQueryVals.cpds_medium_lower_limit}) and (max-width: ${cpdsMediaQueryVals.cpds_medium_upper_limit}) {
        ${HeroEyebrow} {
          font-size: clamp(1.125rem, 0.8432rem + 0.5871vw, 1.3125rem);
          line-height: normal;
        }
        ${HeroContentannel} {
          padding: ${heroVariationObj?.tablet?.padding};
        }
        ${HeroVideoController} {
          bottom: ${token?.spacing?.space_40};
          right: ${token?.spacing?.space_40};
        }
        ${HeroBreadCrumbWrapper} {
          top: 40px;
          left: 40px;
        }
        ${!isToporBottomVariation
          ? css`
              ${HeroImagePannel} {
                margin-left: -40px;
              }
              ${HeroTitleContainer} {
                max-width: clamp(272px, calc((222 / 768) * 100vw), 383px);
              }
              ${HeroDescriptionContainer} {
                max-width: clamp(213px, calc((213 / 768) * 100vw), 287px);
              }
            `
          : css`
              ${HeroDescriptionContainer} {
                max-width: 302px;
              }
            `}

        ${HeroOneKeyLogo} {
          .v3-hero-one-key-logo {
            height: auto;
            max-height: clamp(12px, calc((12 / 768) * 100vw), calc((12 / 768) * 1680px));
            width: clamp(154px, calc((154 / 768) * 100vw), calc((154 / 768) * 1680px));
            max-width: clamp(154px, calc((154 / 768) * 100vw), calc((154 / 768) * 1680px));
            img {
              max-height: clamp(12px, calc((12 / 768) * 100vw), calc((12 / 768) * 1680px));
              object-fit: cover;
            }
          }
        }
      }

      @media only screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
        flex-direction: ${heroVariationObjMobile?.mobile?.direction};
        height: auto;
        padding: ${heroVariationObjMobile?.mobile?.padding};
        ${HeroVideoController} {
          bottom: ${token?.spacing?.space_24};
          right: ${token?.spacing?.space_24};
        }
        ${HeroImagePannel} {
          width: 100%;
          height: auto !important;
          max-width: none;
          aspect-ratio: 1/1;
          margin-left: unset !important;
          .v3-article-hero-image,
          .v3-article-hero-video {
            height: auto !important;
            max-height: none !important;
          }
          ${isOnekey
            ? `
            padding-bottom: 0px !important;
            ${HeroBackgroundOverlay} {
              bottom: 0px !important;
            }
          `
            : ``}
        }
        ${HeroLogoContainer} {
          padding-right: 0;
          .v3-article-hero-partner-logo {
            max-width: 40px;
            max-height: 40px;
          }
        }
        ${HeroOneKeyLogo} {
          .v3-hero-one-key-logo {
            max-width: 190px;
            max-height: 24px;
            width: calc((190 / 390) * 100vw);
            img {
              max-height: 24px;
            }
          }
        }
        ${HeroContentannel} {
          width: 100%;
          max-width: none;
          padding: ${heroVariationObjMobile?.mobile?.padding_content};
          gap: 24px;
        }
        ${HeroSubTextRow} {
          margin-top: 0px;
        }
        ${HeroDescriptionContainer} {
          max-width: 390px;
          display: ${contentStyle === 'MIN' ? 'none' : 'flex'};
        }
        ${HeroEyebrow} {
          font-size: 18px !important;
          line-height: 20px !important;
        }
        ${HeroBreadCrumbWrapper} {
          top: ${heroVariationObjMobile?.mobile?.breadcrumbTop};
          left: ${heroVariationObjMobile?.mobile?.breadCrumbExPadding};
          padding: ${heroVariationObjMobile?.mobile?.breadCrumbExPadding}
            ${heroVariationObjMobile?.mobile?.breadcrumbLeft};
        }
      }

      ${(props) => props?.styleOverrides};
    `;
  }}
`;
